import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AuthObject, AuthProviderContent, IAuthProvider } from './interfaces';
import { useLocation, useNavigate } from 'react-router';
import { useGetCurrentUser, useLogin, useLogout } from '../../reactQuery/auth';
import {
  clearAuthFromStorage,
  getAuthFromStorage,
  saveAuthToStorage,
} from './helpers';

export const AuthContext = createContext<AuthProviderContent>({
  auth: null,
  setAuth: (): void => {},
  handleLogin: async () => false,
  handleLogout: async () => Promise.resolve(),
});

export const AuthProvider: React.FC<IAuthProvider> = ({ children }) => {
  const [auth, setAuth] = useState<AuthObject | null>(() => {
    try {
      return getAuthFromStorage();
    } catch (_error) {
      return null;
    }
  });

  const { data: user } = useGetCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();
  const logoutMutation = useLogout();

  const handleLogin = async (email: string, password: string) => {
    try {
      await loginMutation.mutateAsync({
        email,
        password,
      });

      return true;
    } catch (_error) {
      return false;
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error(error);
    } finally {
      setAuth(null);
      navigate('/login');
    }
  };

  useEffect(() => {
    if (user) {
      setAuth({
        ...auth,
        user: user,
      });

      // The user is set when the user was loaded from memory. In this case we redirect the user
      // to the last known location.
      if (auth?.user) {
        navigate(location.state?.from?.pathname || location.pathname || '/');
      } else {
        navigate('/');
      }
    }
  }, [user]);

  useEffect(() => {
    if (auth) {
      saveAuthToStorage(auth);
    } else {
      clearAuthFromStorage();
    }
  }, [auth]);

  const value = useMemo(
    () => ({
      auth,
      setAuth,
      handleLogout,
      handleLogin,
    }),
    [auth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
